import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { useUsers } from '../../hooks/useUsers';
import {
  GET_CONVERSATION_MEMBERS,
  ADD_CHANNEL_MEMBERS,
  REMOVE_CHANNEL_MEMBER,
  TRANSFER_CHANNEL_OWNERSHIP
} from '../../graphql/operations';
import authService from '../../auth/authService';
import Modal from '../shared/Modal';
import './ManageMembersModal.css';

const INITIAL_ADD_RESULT_LIMIT = 5;

function derivePresence(user) {
  if (!user) return { tone: 'offline', label: 'Offline' };
  if (user.presenceOverride === 'offline') return { tone: 'offline', label: 'Offline' };
  if (!user.lastSeenAt) return { tone: 'offline', label: 'Offline' };

  const lastSeenMs = new Date(user.lastSeenAt).getTime();
  if (!Number.isFinite(lastSeenMs)) return { tone: 'offline', label: 'Offline' };
  const diffMinutes = Math.floor((Date.now() - lastSeenMs) / 60000);
  if (diffMinutes < 2) return { tone: 'online', label: 'Online' };
  return { tone: 'offline', label: 'Offline' };
}

function ManageMembersModal({ conversation, onClose, readOnly = false, titleOverride = '' }) {
  const { refetch: refetchConversations } = useContext(WorkspaceContext);
  const { users } = useUsers();
  const [memberQuery, setMemberQuery] = useState('');
  const [addQuery, setAddQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Inviter chooses whether new members see prior history. Required by the
  // backend mutation (no default). Defaulting the UI to ON matches typical
  // channel UX (Slack-like — new joiners see context). Flip OFF for
  // private/sensitive channels where mid-history rewinds would expose
  // pre-invite discussion.
  const [includeHistory, setIncludeHistory] = useState(true);
  const inputRef = useRef(null);
  const currentUserId = (authService.getEmail() || '').toLowerCase();

  // Fetch live member list with roles
  const { data: membersData, loading: membersLoading, refetch: refetchMembers } = useQuery(
    GET_CONVERSATION_MEMBERS,
    { variables: { conversationId: conversation.id }, fetchPolicy: 'network-only' }
  );

  const membersList = useMemo(
    () => membersData?.getConversationMembers || [],
    [membersData]
  );

  // Determine if current user is the owner
  const currentUserMembership = useMemo(
    () => membersList.find((m) => m.userId === currentUserId),
    [membersList, currentUserId]
  );
  const isOwner = currentUserMembership?.role === 'owner';
  const isAdmin = useMemo(() => {
    const roles = authService.getRoles();
    if (!Array.isArray(roles)) return false;
    return roles.some((role) => {
      const normalized = String(role || '').toLowerCase();
      return normalized === 'admin' || normalized === 'system_admin';
    });
  }, []);
  const canAddMembers = !readOnly && (isOwner || isAdmin);

  // Mutations
  const [addMembers, { loading: adding }] = useMutation(ADD_CHANNEL_MEMBERS);
  const [removeMember, { loading: removing }] = useMutation(REMOVE_CHANNEL_MEMBER);
  const [transferOwnership, { loading: transferring }] = useMutation(TRANSFER_CHANNEL_OWNERSHIP);

  const isBusy = adding || removing || transferring;

  // Enrich member data with display info from users list
  const enrichedMembers = useMemo(() => {
    return membersList.map((m) => {
      const userInfo = users.find((u) => u.username === m.userId);
      const presence = derivePresence(userInfo);
      return {
        userId: m.userId,
        displayName: m.displayName || userInfo?.displayName || m.userId,
        username: userInfo?.username || m.userId,
        role: m.role || 'member',
        presenceTone: presence.tone,
        presenceLabel: presence.label
      };
    });
  }, [membersList, users]);

  const visibleMembers = useMemo(() => {
    if (!memberQuery.trim()) return enrichedMembers;
    const q = memberQuery.toLowerCase();
    return enrichedMembers.filter((member) => (
      String(member.displayName || '').toLowerCase().includes(q)
      || String(member.username || '').toLowerCase().includes(q)
    ));
  }, [enrichedMembers, memberQuery]);

  // Filter users for the "add" dropdown
  const memberUserIds = useMemo(
    () => new Set(membersList.map((m) => m.userId)),
    [membersList]
  );

  const filteredAdd = useMemo(() => {
    const nonAgents = users.filter((u) => !u.isAgent && !memberUserIds.has(u.username));
    if (!addQuery.trim()) return nonAgents;
    const q = addQuery.toLowerCase();
    return nonAgents.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
    );
  }, [addQuery, users, memberUserIds]);

  const visibleAdd = useMemo(() => {
    if (addQuery.trim()) return filteredAdd;
    return filteredAdd.slice(0, INITIAL_ADD_RESULT_LIMIT);
  }, [filteredAdd, addQuery]);

  const handleAdd = useCallback(async (user) => {
    setAddQuery('');
    setShowDropdown(false);
    setHighlightIndex(0);
    setError('');
    try {
      await addMembers({
        variables: {
          conversationId: conversation.id,
          userIds: [user.username],
          includeHistory
        }
      });
      await refetchMembers();
      refetchConversations();
    } catch (err) {
      setError(`Failed to add ${user.displayName}.`);
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [addMembers, conversation.id, includeHistory, refetchMembers, refetchConversations]);

  const handleRemove = useCallback(async (userId) => {
    setError('');
    try {
      await removeMember({ variables: { conversationId: conversation.id, userId } });
      await refetchMembers();
      refetchConversations();
      // If removing self, close the modal
      if (userId === currentUserId) {
        onClose();
      }
    } catch (err) {
      setError(`Failed to remove member.`);
    }
  }, [removeMember, conversation.id, refetchMembers, refetchConversations, currentUserId, onClose]);

  const handleTransferOwnership = useCallback(async (newOwnerId, displayName) => {
    setError('');
    setSuccessMsg('');
    if (!window.confirm(`Transfer ownership to ${displayName}? You will become a regular member.`)) {
      return;
    }
    try {
      await transferOwnership({ variables: { conversationId: conversation.id, newOwnerId } });
      await refetchMembers();
      refetchConversations();
      setSuccessMsg(`Ownership transferred to ${displayName}.`);
    } catch (err) {
      setError('Failed to transfer ownership.');
    }
  }, [transferOwnership, conversation.id, refetchMembers, refetchConversations]);

  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || visibleAdd.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % visibleAdd.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + visibleAdd.length) % visibleAdd.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd(visibleAdd[highlightIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, [showDropdown, visibleAdd, highlightIndex, handleAdd]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(''), 3000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  return (
    <Modal title={titleOverride || `Members — #${conversation.title}`} onClose={onClose}>
      <div className="manage-members">

        {readOnly && (
          <div className="manage-members-section">
            <p className="manage-members-label">Find members</p>
            <div className="manage-members-search">
              <input
                type="text"
                value={memberQuery}
                onChange={(event) => setMemberQuery(event.target.value)}
                placeholder="Search by name or email..."
              />
            </div>
          </div>
        )}

        <div className="manage-members-section">
          <p className="manage-members-label">Current members</p>
          {membersLoading ? (
            <p className="manage-members-empty">Loading members...</p>
          ) : visibleMembers.length === 0 ? (
            <p className="manage-members-empty">No members yet.</p>
          ) : (
            <ul className="manage-members-list">
              {visibleMembers.map((m) => (
                <li key={m.userId} className="manage-members-row">
                  <span className="manage-member-avatar">
                    {(m.displayName || m.userId).charAt(0).toUpperCase()}
                  </span>
                  <div className="manage-member-info">
                    <span className="manage-member-name">
                      {m.displayName}
                      {m.userId === currentUserId && ' (you)'}
                    </span>
                    <span className="manage-member-username">
                      {m.username}
                      <span className={`manage-member-presence ${m.presenceTone}`} aria-hidden="true" />
                      <span className="manage-member-presence-label">{m.presenceLabel}</span>
                    </span>
                  </div>
                  {!readOnly && (
                    <span className={`manage-member-role ${m.role === 'owner' ? 'role-owner' : ''}`}>
                      {m.role}
                    </span>
                  )}
                  {!readOnly && (
                    <div className="manage-member-actions">
                    {/* Owner can make someone else owner */}
                    {isOwner && m.userId !== currentUserId && m.role !== 'owner' && (
                      <button
                        type="button"
                        className="manage-member-transfer"
                        onClick={() => handleTransferOwnership(m.userId, m.displayName)}
                        disabled={isBusy}
                        title="Transfer ownership"
                      >
                        Make owner
                      </button>
                    )}
                    {/* Owner can remove any non-self member */}
                    {isOwner && m.userId !== currentUserId && (
                      <button
                        type="button"
                        className="manage-member-remove"
                        onClick={() => handleRemove(m.userId)}
                        disabled={isBusy}
                        aria-label={`Remove ${m.displayName}`}
                      >
                        Remove
                      </button>
                    )}
                    {/* Non-owners can leave (remove self) */}
                    {!isOwner && m.userId === currentUserId && (
                      <button
                        type="button"
                        className="manage-member-remove"
                        onClick={() => handleRemove(m.userId)}
                        disabled={isBusy}
                      >
                        Leave
                      </button>
                    )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Owners and admins can add members */}
        {canAddMembers && (
          <div className="manage-members-section">
            <p className="manage-members-label">Add members</p>
            {/* History-visibility choice for new members. Backend requires
                an explicit boolean — we surface it here so the inviter
                makes a deliberate decision per channel. Default ON (Slack
                convention); toggle OFF for sensitive channels. */}
            <label className="manage-members-history-toggle">
              <input
                type="checkbox"
                checked={includeHistory}
                onChange={(e) => setIncludeHistory(e.target.checked)}
              />
              <span>New members can see all prior messages</span>
            </label>
            <div className="manage-members-search manage-members-add-search">
              <input
                ref={inputRef}
                type="text"
                value={addQuery}
                onChange={(e) => {
                  setAddQuery(e.target.value);
                  setShowDropdown(true);
                  setHighlightIndex(0);
                }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search by name..."
              />
              {showDropdown && visibleAdd.length > 0 && (
                <div className="manage-members-add-results">
                  {visibleAdd.map((user, idx) => (
                    <button
                      key={user.username}
                      type="button"
                      className={`dm-dropdown-item ${idx === highlightIndex ? 'highlighted' : ''}`}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onMouseDown={(e) => { e.preventDefault(); handleAdd(user); }}
                    >
                      <span className="dm-dropdown-avatar">
                        {(user.displayName || user.username).charAt(0).toUpperCase()}
                      </span>
                      <div className="dm-dropdown-info">
                        <span className="dm-dropdown-name">{user.displayName}</span>
                        <span className="dm-dropdown-username">{user.username}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && addQuery.trim() && visibleAdd.length === 0 && (
                <p className="manage-members-no-results">No matching users.</p>
              )}
            </div>
          </div>
        )}

        {successMsg && <p className="modal-success">{successMsg}</p>}
        {error && <p className="modal-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="modal-btn-secondary" onClick={onClose}>
            {readOnly ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ManageMembersModal;
